import { initBagOfWords } from './bagofwords.js';
import { initEvidence } from './evidence.js';
import { initSyntaxMarking } from './syntax-marking.js';
import { initGapInputs } from './gap.js';
import { initExercises } from './exercises.js';
import { initReading } from './reading.js';
import { initArcs } from './arcs.js';
import { initGrammarHighlighters } from './grammar-init.js';
import {
    initKatex,
    initToc,
    initMobileNav,
    initCopyButtons,
    initBackToTop,
    initLiveReload,
} from './page-chrome.js';

let stickyMarginNotesCleanup = null;
let tocOverlapCleanup = null;

function hasRenderableMarginContent(note) {
    if (!note) {
        return false;
    }

    const meaningfulChild = note.querySelector('img, svg, video, audio, iframe, object, embed, table, pre, blockquote, ul, ol, dl, figure, .column-page, .column-page-inset, .column-screen, .column-screen-inset, .syntax, .sentence, div');
    if (meaningfulChild) {
        return true;
    }

    return note.textContent.trim().length > 0;
}

function syncMarginLayoutMode() {
    const hasMarginMaterial = Array.from(document.querySelectorAll('div.column-margin')).some(hasRenderableMarginContent);
    document.body.classList.toggle('ph-has-margin-material', hasMarginMaterial);
    document.body.classList.toggle('ph-no-margin-material', !hasMarginMaterial);
}

function hashCode(str) {
    var hash = 0, i, chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
        chr   = str.charCodeAt(i);
        hash  = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

function initStickyMarginNotes() {
    if (stickyMarginNotesCleanup) {
        stickyMarginNotesCleanup();
        stickyMarginNotesCleanup = null;
    }

    const desktopMq = window.matchMedia('(min-width: 72rem)');
    const notes = Array.from(document.querySelectorAll('div.column-margin.sticky'));
    if (notes.length === 0) {
        return;
    }

    const stickyTop = 12;
    const noteGap = 24;
    const jitterThreshold = 6;
    const anchors = notes.map((note) => {
        const anchor = document.createElement('span');
        anchor.setAttribute('aria-hidden', 'true');
        anchor.style.cssText = 'display:block;position:relative;width:0;height:0;clear:both;';
        note.parentNode.insertBefore(anchor, note);
        return anchor;
    });

    let rafId = 0;
    let lastScrollY = window.scrollY || window.pageYOffset || 0;
    let currentOffsets = new Array(notes.length).fill(0);
    let handoffActive = new Array(notes.length).fill(false);

    const reset = () => {
        currentOffsets = new Array(notes.length).fill(0);
        handoffActive = new Array(notes.length).fill(false);
        lastScrollY = window.scrollY || window.pageYOffset || 0;
        notes.forEach((note) => {
            note.style.removeProperty('transform');
            note.style.removeProperty('z-index');
        });
    };

    const layout = () => {
        rafId = 0;

        if (!desktopMq.matches) {
            reset();
            return;
        }

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const maxStep = Math.abs(scrollY - lastScrollY);
        const scrollingDown = scrollY >= lastScrollY;

        notes.forEach((note, index) => {
            const currentOffset = currentOffsets[index];
            const nextNote = notes[index + 1];
            let offset = currentOffset;

            if (nextNote) {
                const noteRect = note.getBoundingClientRect();
                const nextRect = nextNote.getBoundingClientRect();
                const collisionTop = stickyTop + note.offsetHeight + noteGap;
                const handoffStartThreshold = collisionTop + 24;
                const handoffReleaseThreshold = collisionTop + 48;
                const overlap = noteRect.bottom + noteGap - nextRect.top;

                if (scrollingDown && nextRect.top <= handoffStartThreshold) {
                    handoffActive[index] = true;
                } else if (!scrollingDown && nextRect.top > handoffReleaseThreshold) {
                    handoffActive[index] = false;
                }

                if (handoffActive[index] && overlap > 0) {
                    offset = currentOffset - Math.max(overlap, jitterThreshold);
                } else if (currentOffset < 0) {
                    offset = Math.min(0, currentOffset + Math.max(maxStep, 1));
                }
            } else if (currentOffset < 0) {
                offset = Math.min(0, currentOffset + Math.max(maxStep, 1));
            }

            if (scrollingDown) {
                offset = Math.min(offset, currentOffset);
            }

            if (maxStep > 0) {
                const delta = offset - currentOffset;
                if (Math.abs(delta) > maxStep) {
                    offset = currentOffset + Math.sign(delta) * maxStep;
                }
            }

            offset = Math.round(offset);
            currentOffsets[index] = offset;
            note.style.transform = offset === 0 ? '' : `translate3d(0, ${offset}px, 0)`;
            note.style.zIndex = String(10 + index);
        });

        lastScrollY = scrollY;
    };

    const schedule = () => {
        if (rafId !== 0) return;
        rafId = window.requestAnimationFrame(layout);
    };

    const handleChange = () => schedule();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (typeof desktopMq.addEventListener === 'function') {
        desktopMq.addEventListener('change', handleChange);
    } else if (typeof desktopMq.addListener === 'function') {
        desktopMq.addListener(handleChange);
    }

    schedule();

    stickyMarginNotesCleanup = () => {
        if (rafId !== 0) {
            window.cancelAnimationFrame(rafId);
            rafId = 0;
        }
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        if (typeof desktopMq.removeEventListener === 'function') {
            desktopMq.removeEventListener('change', handleChange);
        } else if (typeof desktopMq.removeListener === 'function') {
            desktopMq.removeListener(handleChange);
        }
        reset();
        anchors.forEach((anchor) => anchor.remove());
    };
}

function initTocOverlap() {
    if (tocOverlapCleanup) {
        tocOverlapCleanup();
        tocOverlapCleanup = null;
    }

    const tocCol = document.querySelector('.ph-toc-col');
    if (!tocCol) {
        return;
    }
    const tocNav = tocCol.querySelector('#ph-toc');
    const tocHeading = tocCol.querySelector('.ph-toc-heading');
    if (!tocNav) {
        return;
    }

    const marginEls = Array.from(document.querySelectorAll('div.column-margin'));
    if (marginEls.length === 0) {
        return;
    }

    const desktopMq = window.matchMedia('(min-width: 1200px)');
    const PADDING = 12;

    let toggleEl = null;
    let dropdownEl = null;
    let folded = false;
    let dropdownOpen = false;
    let rafId = 0;
    let outsideClickListener = null;

    const setDropdown = (open) => {
        if (!toggleEl || !dropdownEl) return;
        if (open === dropdownOpen) return;
        dropdownOpen = open;
        toggleEl.classList.toggle('ph-toc-toggle-open', open);
        dropdownEl.classList.toggle('ph-toc-dropdown-open', open);
        toggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
        dropdownEl.setAttribute('aria-hidden', open ? 'false' : 'true');
    };

    const positionToggle = () => {
        if (!toggleEl) return;
        const rect = tocCol.getBoundingClientRect();
        toggleEl.style.left = `${rect.left}px`;
        toggleEl.style.top = `${rect.top}px`;
        toggleEl.style.width = `${rect.width}px`;
        if (dropdownEl) {
            dropdownEl.style.left = `${rect.left}px`;
            dropdownEl.style.top = `${rect.top + toggleEl.offsetHeight + 4}px`;
            dropdownEl.style.width = `${rect.width}px`;
        }
    };

    const buildToggle = () => {
        if (toggleEl) return;

        toggleEl = document.createElement('div');
        toggleEl.className = 'ph-toc-toggle';
        toggleEl.setAttribute('role', 'button');
        toggleEl.setAttribute('aria-expanded', 'false');
        toggleEl.setAttribute('aria-controls', 'ph-toc-dropdown');
        toggleEl.setAttribute('tabindex', '0');

        const titleSpan = document.createElement('span');
        titleSpan.className = 'ph-toc-toggle-title';
        titleSpan.textContent = (tocHeading?.textContent || 'On This Page').trim();
        const caret = document.createElement('span');
        caret.className = 'ph-toc-toggle-caret';
        caret.setAttribute('aria-hidden', 'true');
        caret.textContent = '▾';
        toggleEl.append(titleSpan, caret);

        dropdownEl = document.createElement('div');
        dropdownEl.id = 'ph-toc-dropdown';
        dropdownEl.className = 'ph-toc-dropdown';
        dropdownEl.setAttribute('aria-hidden', 'true');
        if (tocHeading) {
            dropdownEl.appendChild(tocHeading.cloneNode(true));
        }
        dropdownEl.appendChild(tocNav.cloneNode(true));

        document.body.appendChild(toggleEl);
        document.body.appendChild(dropdownEl);

        const onToggle = () => setDropdown(!dropdownOpen);
        toggleEl.addEventListener('click', onToggle);
        toggleEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle();
            }
        });

        // Close when an entry inside the dropdown is clicked.
        dropdownEl.addEventListener('click', (e) => {
            if (e.target.closest('a')) setDropdown(false);
        });

        outsideClickListener = (e) => {
            if (!dropdownOpen) return;
            if (toggleEl.contains(e.target) || dropdownEl.contains(e.target)) return;
            setDropdown(false);
        };
        document.addEventListener('click', outsideClickListener);
    };

    const setFolded = (next) => {
        if (next === folded) {
            if (folded) positionToggle();
            return;
        }
        folded = next;
        if (folded) {
            buildToggle();
            tocCol.classList.add('ph-toc-col-folded');
            toggleEl.classList.add('ph-toc-toggle-active');
            positionToggle();
        } else {
            tocCol.classList.remove('ph-toc-col-folded');
            if (toggleEl) toggleEl.classList.remove('ph-toc-toggle-active');
            setDropdown(false);
        }
    };

    const checkOverlap = () => {
        rafId = 0;
        if (!desktopMq.matches) {
            setFolded(false);
            return;
        }
        const tocRect = tocCol.getBoundingClientRect();
        if (tocRect.height === 0 || tocRect.width === 0) {
            setFolded(false);
            return;
        }
        const tocTop = tocRect.top - PADDING;
        const tocBottom = tocRect.bottom + PADDING;

        let overlap = false;
        for (const el of marginEls) {
            const r = el.getBoundingClientRect();
            if (r.height === 0 && r.width === 0) continue;
            if (r.bottom < tocTop) continue;
            if (r.top > tocBottom) continue;
            // Margin notes float into the right rail; their right edge sits
            // close to the TOC column's right edge, so any element whose rect
            // is entirely left of the TOC can be skipped.
            if (r.right < tocRect.left) continue;
            overlap = true;
            break;
        }
        setFolded(overlap);
    };

    const schedule = () => {
        if (rafId !== 0) return;
        rafId = window.requestAnimationFrame(checkOverlap);
    };

    const onScroll = () => {
        if (dropdownOpen) setDropdown(false);
        schedule();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', schedule);
    if (typeof desktopMq.addEventListener === 'function') {
        desktopMq.addEventListener('change', schedule);
    } else if (typeof desktopMq.addListener === 'function') {
        desktopMq.addListener(schedule);
    }

    schedule();

    tocOverlapCleanup = () => {
        if (rafId !== 0) {
            window.cancelAnimationFrame(rafId);
            rafId = 0;
        }
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', schedule);
        if (typeof desktopMq.removeEventListener === 'function') {
            desktopMq.removeEventListener('change', schedule);
        } else if (typeof desktopMq.removeListener === 'function') {
            desktopMq.removeListener(schedule);
        }
        if (outsideClickListener) {
            document.removeEventListener('click', outsideClickListener);
            outsideClickListener = null;
        }
        if (toggleEl) toggleEl.remove();
        if (dropdownEl) dropdownEl.remove();
        tocCol.classList.remove('ph-toc-col-folded');
    };
}

function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}

// Run one page feature. A throw in one (a malformed data-solution, say) is
// logged and must not stop every feature initialised after it.
function step(name, fn) {
    try {
        fn();
    } catch (err) {
        console.error(`[pantarhei] ${name} failed:`, err);
    }
}

const initPage = () => {
    // quarto:load and DOMContentLoaded can both fire; a second run would bind
    // every listener twice and empty the text-tag blocks.
    if (document.documentElement.hasAttribute('data-ph-init')) return;
    document.documentElement.setAttribute('data-ph-init', '');

    step('margin layout', syncMarginLayoutMode);
    step('grammar highlighter', initGrammarHighlighters);
    step('syntax marking', initSyntaxMarking);
    step('evidence', initEvidence);
    step('reading', initReading);
    step('arcs', initArcs);
    step('exercises', initExercises);   // before the gap widgets: marks deferred gaps, adds the revisit block
    step('gaps', initGapInputs);
    step('bag of words', initBagOfWords);

    // TEXT TAGGING

    const colorTags = ["column-color-tag", "row-color-tag", "cell-color-tag"];
    
    function compareColorTags(a, b) {
        const aScore = colorTags.indexOf(a.classList[0]);
        const bScore = colorTags.indexOf(b.classList[0]);
        return aScore - bScore;
    }
    
    function shuffleTables() {
        document.querySelectorAll("table[data-shuffle]").forEach(table => {
            const tbody = table.querySelector("tbody");
            if (tbody) {
                const rows = Array.from(tbody.querySelectorAll("tr"));
                const shuffledRows = shuffleArray([...rows]);
            
                // Remove all rows
                rows.forEach(row => row.remove());
            
                // Add back in shuffled order
                shuffledRows.forEach(row => tbody.appendChild(row));
            }
        });
    }

    function applyColors() {
        const spans = document.querySelectorAll("span.column-color-tag, span.row-color-tag, span.cell-color-tag");
        const sortedSpans = Array.from(spans).sort(compareColorTags);
        
        sortedSpans.forEach(span => {
            const color = span.getAttribute("data-background-color");
            if (span.classList.contains("column-color-tag")) {
                const cell = span.closest("td, th");
                const columnIndex = Array.from(cell.parentNode.children).indexOf(cell);
                const table = cell.closest("table");
                
                table.querySelectorAll("tr").forEach(row => {
                    const rowCell = row.children[columnIndex];
                    if (rowCell) {
                        rowCell.style.backgroundColor = color;
                    }
                });
            } else if (span.classList.contains("row-color-tag")) {
                const row = span.closest("tr");
                row.style.backgroundColor = color;
                row.querySelectorAll("td, th").forEach(cell => {
                    cell.style.backgroundColor = "";
                });
            } else if (span.classList.contains("cell-color-tag")) {
                const cell = span.closest("td, th");
                cell.style.backgroundColor = color;
            }
        });
        
        document.querySelectorAll("td, th").forEach(cell => {
            const cellColorTag = cell.querySelector("span.cell-color-tag");
            if (cellColorTag) {
                const color = cellColorTag.getAttribute("data-background-color");
                cell.style.backgroundColor = color;
            }
        });
    }
    
    step('color tags', applyColors);
    step('table shuffle', shuffleTables);
    step('sticky margin notes', initStickyMarginNotes);
    step('toc overlap', initTocOverlap);

    // Page chrome shared with site mode and standalone mode. Each is a
    // graceful no-op when the markup it expects isn't present.
    step('katex', initKatex);
    step('toc', initToc);
    step('mobile nav', initMobileNav);
    step('copy buttons', initCopyButtons);
    step('back to top', initBackToTop);
    step('live reload', initLiveReload);

    step('text tags', initTextTags);
};

function initTextTags() {
    document.querySelectorAll("div.text-tag, div.texttag, div.tag").forEach((div, index) => {
        let hash = hashCode(div.innerHTML + index);

        div.setAttribute("data-hash", hash);

        let textTags = Array.from(div.querySelectorAll("span.text-tag")).map(span => span.getAttribute("data-text-tag"));
        const uniqueTextTags = shuffleArray(Array.from(new Set(textTags)));

        const p = document.createElement("p");
        uniqueTextTags.forEach(textTag => {
            const inputId = `${hash}${textTag}`;

            const input = document.createElement("input");
            input.type = "radio";
            input.id = inputId;
            input.className = textTag;
            input.name = String(hash);
            input.value = textTag;

            const label = document.createElement("label");
            label.htmlFor = inputId;
            label.className = textTag;
            label.textContent = textTag;

            p.appendChild(input);
            p.appendChild(label);
        });
        while (div.firstChild) { div.removeChild(div.firstChild); }
        div.appendChild(p);
    });
}

document.addEventListener("quarto:load", initPage);
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPage);
} else {
    initPage();
}
